/**
 * Admin Pickup Locations Management Page
 * Allows admins to create, view, update, and delete pickup locations
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useUIStore } from '@/stores/uiStore';
import {
  fetchPickupLocations,
  deletePickupLocation,
} from '@/lib/api/pickupLocations';
import { LocationColorBadge } from '@/components/ui/location-color-badge';
import type { PickupLocation } from '@/types/api';
import {
  CreatePickupLocationModal,
  EditPickupLocationModal,
} from '@/components/admin/PickupLocationModal';
import { DeletePickupLocationDialog } from '@/components/admin/DeletePickupLocationDialog';

export function PickupLocations() {
  const [locations, setLocations] = useState<PickupLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<PickupLocation | null>(null);
  const [deletingLocation, setDeletingLocation] = useState<PickupLocation | null>(null);
  const { showToast } = useUIStore();

  // Load locations
  const loadLocations = async () => {
    try {
      setLoading(true);
      const data = await fetchPickupLocations(false); // Get all locations (active and inactive)
      setLocations(data);
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to load pickup locations',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLocations();
  }, []);

  const handleDelete = async () => {
    if (!deletingLocation) return;

    try {
      await deletePickupLocation(deletingLocation.id);
      showToast('Pickup location deleted successfully', 'success');
      setDeletingLocation(null);
      loadLocations();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to delete pickup location',
        'error'
      );
    }
  };

  return (
    <div className="container mx-auto p-8 max-w-6xl">
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-muted-foreground mt-1">
            Manage pickup locations for meal distribution
          </p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          Create Location
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : locations.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">No pickup locations found</p>
          <Button onClick={() => setCreateModalOpen(true)}>
            Create First Location
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {locations.map((location) => (
            <Card key={location.id} className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <LocationColorBadge colorCode={location.colorCode} className="px-2.5 text-sm font-semibold">
                      {location.name}
                    </LocationColorBadge>
                    <Badge variant={location.isActive ? 'success' : 'secondary'}>
                      {location.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  {location.address && (
                    <p className="text-muted-foreground">{location.address}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingLocation(location)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeletingLocation(location)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <CreatePickupLocationModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={() => {
          setCreateModalOpen(false);
          loadLocations();
        }}
      />

      {/* Edit Modal */}
      {editingLocation && (
        <EditPickupLocationModal
          location={editingLocation}
          open={!!editingLocation}
          onOpenChange={(open) => !open && setEditingLocation(null)}
          onSuccess={() => {
            setEditingLocation(null);
            loadLocations();
          }}
        />
      )}

      {/* Delete Dialog */}
      {deletingLocation && (
        <DeletePickupLocationDialog
          location={deletingLocation}
          open={!!deletingLocation}
          onOpenChange={(open) => !open && setDeletingLocation(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
